import React from 'react';
import PublicLegalLayout from '../Components/PublicLegalLayout';

function PrivacyPolicy() {
    const lastUpdated = new Date().toLocaleDateString();

    return (
        <PublicLegalLayout
            title="Privacy Policy"
            lastUpdated={lastUpdated}
            description="How CueClub Manager collects, uses, and protects club, player, scoreboard, and account data."
        >
            <div className="card legal-document-card">
                <div className="card-body">
                    <div className="legal-document-copy">

                            <section className="mb-4">
                                <h2 className="h3 mb-3">1. Introduction</h2>
                                <p>
                                    This Privacy Policy describes how our Club Management System ("we," "us," or "our") collects, uses,
                                    and protects your personal information when you use our platform for managing gaming clubs, including
                                    snooker, pool, US pool, and 8-ball facilities.
                                </p>
                                <p>
                                    We are committed to protecting your privacy and ensuring the security of your personal data in
                                    accordance with applicable data protection laws.
                                </p>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">2. Information We Collect</h2>

                                <h4 className="h5 mb-2">2.1 Personal Information</h4>
                                <ul className="mb-3">
                                    <li><strong>Account Information:</strong> Name, email address, phone number, club membership details</li>
                                    <li><strong>Player Profiles:</strong> Gaming preferences, skill levels, tournament history</li>
                                    <li><strong>Contact Details:</strong> Billing address, emergency contact information</li>
                                    <li><strong>Payment Information:</strong> Credit card details, billing history (processed securely through third-party providers)</li>
                                </ul>

                                <h4 className="h5 mb-2">2.2 Gaming Data</h4>
                                <ul className="mb-3">
                                    <li><strong>Game Statistics:</strong> Match results, scores, game duration, performance metrics</li>
                                    <li><strong>Tournament Records:</strong> Participation history, rankings, achievements</li>
                                    <li><strong>Equipment Usage:</strong> Table reservations, equipment preferences, usage patterns</li>
                                </ul>

                                <h4 className="h5 mb-2">2.3 Technical Information</h4>
                                <ul className="mb-3">
                                    <li><strong>Usage Data:</strong> Log-in times, features accessed, system interactions</li>
                                    <li><strong>Device Information:</strong> IP address, browser type, operating system</li>
                                    <li><strong>Cookies:</strong> Session data, user preferences, authentication tokens</li>
                                </ul>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">3. How We Use Your Information</h2>

                                <h4 className="h5 mb-2">3.1 Service Provision</h4>
                                <ul className="mb-3">
                                    <li>Managing your club membership and player profiles</li>
                                    <li>Operating scoreboards and tracking game statistics</li>
                                    <li>Processing payments and generating financial reports</li>
                                    <li>Facilitating tournament organization and player rankings</li>
                                </ul>

                                <h4 className="h5 mb-2">3.2 Communication</h4>
                                <ul className="mb-3">
                                    <li>Sending important account notifications and updates</li>
                                    <li>Tournament announcements and club event information</li>
                                    <li>Technical support and customer service communications</li>
                                    <li>Marketing communications (with your consent)</li>
                                </ul>

                                <h4 className="h5 mb-2">3.3 Analytics and Improvement</h4>
                                <ul className="mb-3">
                                    <li>Analyzing usage patterns to improve our platform</li>
                                    <li>Generating anonymized reports for club management insights</li>
                                    <li>Enhancing user experience and platform functionality</li>
                                </ul>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">4. Information Sharing and Disclosure</h2>
                                <p>We do not sell, trade, or otherwise transfer your personal information to third parties except in the following circumstances:</p>

                                <h4 className="h5 mb-2">4.1 Service Providers</h4>
                                <ul className="mb-3">
                                    <li>Payment processors for secure transaction handling</li>
                                    <li>Cloud hosting providers for data storage and processing</li>
                                    <li>Analytics services for platform improvement</li>
                                </ul>

                                <h4 className="h5 mb-2">4.2 Legal Requirements</h4>
                                <ul className="mb-3">
                                    <li>When required by law or legal process</li>
                                    <li>To protect our rights, property, or safety</li>
                                    <li>In connection with business transfers or acquisitions</li>
                                </ul>

                                <h4 className="h5 mb-2">4.3 Club Community Features</h4>
                                <ul className="mb-3">
                                    <li>Tournament leaderboards and public rankings</li>
                                    <li>Match results and statistics (as configured by club settings)</li>
                                    <li>Club member directories (with appropriate privacy controls)</li>
                                </ul>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">5. Data Security</h2>
                                <p>We implement industry-standard security measures to protect your personal information:</p>
                                <ul className="mb-3">
                                    <li><strong>Encryption:</strong> All data transmission is encrypted using SSL/TLS protocols</li>
                                    <li><strong>Access Controls:</strong> Strict user authentication and authorization systems</li>
                                    <li><strong>Data Backup:</strong> Regular automated backups with secure storage</li>
                                    <li><strong>Security Monitoring:</strong> Continuous monitoring for unauthorized access</li>
                                    <li><strong>Staff Training:</strong> Regular security awareness training for our team</li>
                                </ul>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">6. Data Retention</h2>
                                <p>We retain your personal information for as long as necessary to:</p>
                                <ul className="mb-3">
                                    <li>Provide our services and maintain your account</li>
                                    <li>Comply with legal obligations and resolve disputes</li>
                                    <li>Maintain historical gaming statistics and tournament records</li>
                                </ul>
                                <p>
                                    Account data is typically retained for 7 years after account closure for legal and
                                    business purposes. Game statistics may be retained longer for historical records.
                                </p>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">7. Your Rights</h2>
                                <p>You have the following rights regarding your personal information:</p>

                                <h4 className="h5 mb-2">7.1 Access and Portability</h4>
                                <ul className="mb-3">
                                    <li>Request copies of your personal data</li>
                                    <li>Export your gaming statistics and tournament history</li>
                                    <li>Download your account information in a portable format</li>
                                </ul>

                                <h4 className="h5 mb-2">7.2 Correction and Updates</h4>
                                <ul className="mb-3">
                                    <li>Update your profile information through your account settings</li>
                                    <li>Correct inaccurate personal or gaming data</li>
                                    <li>Request manual corrections for system errors</li>
                                </ul>

                                <h4 className="h5 mb-2">7.3 Deletion and Restriction</h4>
                                <ul className="mb-3">
                                    <li>Delete your account and associated personal data</li>
                                    <li>Restrict processing of specific information types</li>
                                    <li>Opt-out of marketing communications</li>
                                </ul>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">8. Cookies and Tracking</h2>
                                <p>Our platform uses cookies and similar technologies to:</p>
                                <ul className="mb-3">
                                    <li>Maintain your login session and user preferences</li>
                                    <li>Remember your scoreboard and game settings</li>
                                    <li>Analyze platform usage and performance</li>
                                    <li>Provide personalized club recommendations</li>
                                </ul>
                                <p>
                                    You can manage cookie preferences through your browser settings. Disabling certain
                                    cookies may limit platform functionality.
                                </p>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">9. International Data Transfers</h2>
                                <p>
                                    Your data may be processed in countries other than your own. We ensure appropriate
                                    safeguards are in place for international data transfers, including:
                                </p>
                                <ul className="mb-3">
                                    <li>Adequacy decisions by relevant data protection authorities</li>
                                    <li>Standard contractual clauses with service providers</li>
                                    <li>Certification under recognized privacy frameworks</li>
                                </ul>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">10. Children's Privacy</h2>
                                <p>
                                    Our platform may be used by minors participating in club activities. We take special
                                    care to protect children's privacy:
                                </p>
                                <ul className="mb-3">
                                    <li>Parental consent is required for users under 13 years old</li>
                                    <li>Limited data collection for minor participants</li>
                                    <li>Enhanced privacy controls for junior player profiles</li>
                                    <li>Restricted access to certain community features</li>
                                </ul>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">11. Updates to This Policy</h2>
                                <p>
                                    We may update this Privacy Policy periodically to reflect changes in our practices
                                    or legal requirements. We will notify you of significant changes through:
                                </p>
                                <ul className="mb-3">
                                    <li>Email notifications to your registered address</li>
                                    <li>In-platform notifications when you log in</li>
                                    <li>Updates posted on our website</li>
                                </ul>
                                <p>Continued use of our platform constitutes acceptance of the updated policy.</p>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">12. Contact Information</h2>
                                <div className="alert alert-info legal-contact-box">
                                    <p className="mb-2"><strong>Privacy Questions or Concerns?</strong></p>
                                    <p className="mb-0">Email: <a href="mailto:contact@cueclubmanager.com">contact@cueclubmanager.com</a></p>
                                </div>
                            </section>
                    </div>
                </div>
            </div>
        </PublicLegalLayout>
    );
}

export default PrivacyPolicy;
